import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import ThemeSelectionModal from './ThemeSelectionModal';
import { useAuth } from '../contexts/AuthContext';
import {
  canGenerateFreshThemeSet,
  canGenerateMatchingPlan,
  getEffectiveBillingUser
} from '../utils/billing';
import { getThemePlanKey, getConceptLabelForIntent } from '../utils/themePlanKeys';
import { getIntentCtaLabel } from '../utils/planningIntent';

const normalizeHexColor = (value = '') => {
  const raw = String(value || '').trim().replace('#', '');
  if (raw.length === 3) {
    return raw.split('').map((char) => char + char).join('');
  }
  return raw.length === 6 ? raw : '';
};

const isLightColor = (value = '') => {
  const hex = normalizeHexColor(value);
  if (!hex) return false;
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  return (r + g + b) > 630;
};

const parseAndFormatScore = (score) => {
  if (score === null || score === undefined) return null;
  const str = String(score).trim();
  if (str === '') return null;
  const num = Number(str);
  if (Number.isNaN(num)) return null;
  if (num < 0 || num > 10) return null;
  return num.toFixed(1);
};

const getFirstPlannerBadge = (theme) => {
  const badges = theme?.planner_badges;
  if (Array.isArray(badges)) {
    const first = badges.find(b => typeof b === 'string' && b.trim() !== '');
    return first ? first.trim() : null;
  }
  return null;
};

/** Small self-contained component: row of colour circles.
 *  Tapping/clicking or hovering a circle reveals its name instantly. */
const SwatchRow = ({ palette }) => {
  const [visibleTooltip, setVisibleTooltip] = React.useState(null);
  const timerRef = React.useRef(null);

  const handleTap = (i) => {
    clearTimeout(timerRef.current);
    setVisibleTooltip({ index: i, source: 'tap' });
    timerRef.current = setTimeout(() => setVisibleTooltip(null), 1800);
  };

  React.useEffect(() => () => clearTimeout(timerRef.current), []);

  return (
    <div className="pb-2 sm:pb-5 shrink-0 flex flex-wrap items-center gap-2 relative">
      {palette.map((c, i) => {
        const hex = typeof c === 'string' ? c : c?.hex || '#e5e7eb';
        const rawName = typeof c === 'string' ? hex : c?.name || hex;
        const displayName = rawName !== hex ? rawName : hex;
        const isActive = visibleTooltip?.index === i;
        const inactiveBorderClass = isLightColor(hex)
          ? 'border-gray-300 ring-1 ring-gray-200'
          : 'border-white';
        const tooltipAlignClass = i === 0
          ? 'left-0 translate-x-0'
          : i === palette.length - 1
            ? 'right-0 translate-x-0'
            : 'left-1/2 -translate-x-1/2';

        return (
          <div key={i} className="relative flex flex-col items-center">
            <button
              type="button"
              aria-label={displayName}
              onClick={(e) => { e.stopPropagation(); handleTap(i); }}
              onMouseEnter={() => {
                clearTimeout(timerRef.current);
                setVisibleTooltip({ index: i, source: 'hover' });
              }}
              onMouseLeave={() => {
                clearTimeout(timerRef.current);
                setVisibleTooltip(null);
              }}
              onFocus={() => {
                clearTimeout(timerRef.current);
                setVisibleTooltip({ index: i, source: 'focus' });
              }}
              onBlur={() => setVisibleTooltip(null)}
              className={`rounded-full border-2 shrink-0 shadow-sm ${isActive ? 'border-blue-500 ring-2 ring-blue-100' : inactiveBorderClass}`}
              style={{ backgroundColor: hex, width: 28, height: 28 }}
            />
            {isActive && (
              <div className={`absolute top-full mt-2 z-20 whitespace-nowrap ${tooltipAlignClass}`}>
                <span className="block rounded-md border border-gray-100 bg-white px-2 py-1 font-sans text-[10px] font-bold leading-none text-gray-700 shadow-[0_2px_8px_rgba(0,0,0,0.12)]">
                  {displayName}
                </span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const stripInternalThemeDirection = (value = '') => (
  String(value || '').replace(/\n{0,2}Similar-theme direction:[\s\S]*$/i, '').trim()
);

const ThemeCarousel = ({
  themes,
  onThemeSelect,
  onRegenerate,
  onBackToInput,
  eventData,
  isAuthenticated,
  onRequestAuth,
  onBuyCredits,
  selectionPreferences,
  onUpdateSelectionPreferences,
  selectedTheme,
  themePlans = {},
  onViewThemePlan,
  hasShoppingItems = false,
  onEditTheme,
  sidebarCollapsed = false,
  onThemeFocusChange,
  favoriteThemeKeys = [],
  onToggleThemeFavorite = null,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isGridMode = windowWidth >= 640;
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [freshSetModal, setFreshSetModal] = useState(null);
  const [freshSetCount, setFreshSetCount] = useState(4);
  const [freshSetMode, setFreshSetMode] = useState('append');
  const [freshSetSummary, setFreshSetSummary] = useState({
    event_type: '',
    guest_count: '',
    budget: '',
    location: '',
    event_date: '',
    theme_input: '',
    additional_details: ''
  });
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [pendingDeleteGroup, setPendingDeleteGroup] = useState(null);
  // 'all' | 'favorites' | <group.id>
  const [activeGroupId, setActiveGroupId] = useState('all');
  const activePillRef = React.useRef(null);

  useEffect(() => {
    if (activePillRef.current) {
      activePillRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    }
  }, [activeGroupId]);
  const [isCarouselDragging, setIsCarouselDragging] = useState(false);
  const carouselViewportRef = useRef(null);
  const moreMenuRef = useRef(null);
  const { user } = useAuth();
  const effectiveBillingUser = getEffectiveBillingUser(user);

  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [modalImgIdx, setModalImgIdx] = useState(0);

  const getThemeKey = (theme) => String(getThemePlanKey(theme) || theme?.id || theme?.name || '').trim();
  const explicitFocusThemeKey = String(eventData?.__focusThemeKey || eventData?.__returnThemeKey || '').trim();
  const appliedFocusThemeKeyRef = useRef('');
  const restoredThemeSessionRef = useRef(false);
  const themeSessionStorageKey = useMemo(() => {
    const scopedPlanId = String(
      eventData?.plan_id
      || eventData?.id
      || eventData?._id
      || eventData?.draft_id
      || eventData?.sessionId
      || 'draft'
    ).trim();
    return `craftmyparty_theme_session_${scopedPlanId}`;
  }, [eventData]);

  // Close more menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(event.target)) {
        setShowMoreMenu(false);
      }
    };

    if (showMoreMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMoreMenu]);

  const themeGroups = useMemo(() => {
    if (!Array.isArray(themes) || themes.length <= 0) return [];

    const groups = [];
    const originalIndices = [];
    themes.forEach((theme, index) => {
      if (!theme?.theme_set_id && !theme?.variant_of_theme_key && !theme?.variant_of_theme_name) {
        originalIndices.push(index);
      }
    });
    if (originalIndices.length > 0) {
      groups.push({
        id: 'original',
        label: 'Original set',
        firstIndex: originalIndices[0],
        count: originalIndices.length
      });
    }

    const variantGroups = new Map();
    themes.forEach((theme, index) => {
      const groupKey = theme?.theme_set_id || theme?.variant_of_theme_key || theme?.variant_of_theme_name;
      if (!groupKey) return;
      if (!variantGroups.has(groupKey)) {
        const parentTheme = themes.find((candidate) => getThemeKey(candidate) === theme?.variant_of_theme_key);
        const parentName = theme?.variant_of_theme_name || parentTheme?.name || '';
        const rawLabel = String(theme?.theme_set_label || '').trim();
        const genericSetLabel = /^set\s+\d+$/i.test(rawLabel);
        // Priority:
        //   - A generic "Set N" label always wins — it indicates a fresh-set
        //     generation, even if a stale variant_of_theme_name slipped in.
        //   - Otherwise, if there's a real parent (similar-mode), show
        //     "Inspired by <X>".
        //   - Final fallback is whatever rawLabel says, or a generic Set N.
        let label;
        if (genericSetLabel) {
          label = rawLabel;
        } else if (parentName) {
          label = `Inspired by ${parentName}`;
        } else if (rawLabel) {
          label = rawLabel;
        } else {
          label = `Set ${variantGroups.size + 2}`;
        }
        variantGroups.set(groupKey, {
          id: String(groupKey),
          label,
          // A generic "Set N" label always classifies as a fresh theme set,
          // even if a stale variant_of_theme_name was attached.
          isInspired: !genericSetLabel && (Boolean(parentName) || /^inspired by /i.test(rawLabel)),
          genericSetLabel,
          firstIndex: index,
          count: 0
        });
      }
      variantGroups.get(groupKey).count += 1;
    });

    return [...groups, ...Array.from(variantGroups.values())];
  }, [themes]); // eslint-disable-line react-hooks/exhaustive-deps



  const getThemeGroupId = (theme) => {
    if (!theme?.theme_set_id && !theme?.variant_of_theme_key && !theme?.variant_of_theme_name) {
      return 'original';
    }
    return String(theme?.theme_set_id || theme?.variant_of_theme_key || theme?.variant_of_theme_name || '');
  };

  const carouselThemes = useMemo(() => {
    if (!Array.isArray(themes) || themes.length === 0) return [];
    let list = themes.map((theme, originalIndex) => ({ theme, originalIndex }));
    if (activeGroupId === 'favorites') {
      list = list.filter(({ theme }) => favoriteThemeKeys.includes(getThemeKey(theme)));
    } else if (activeGroupId !== 'all') {
      list = list.filter(({ theme }) => getThemeGroupId(theme) === activeGroupId);
    }
    const groupOrder = themeGroups.map((g) => g.id);
    const groupRank = (theme) => {
      const gid = getThemeGroupId(theme);
      const idx = groupOrder.indexOf(gid);
      return idx === -1 ? groupOrder.length : idx;
    };
    list.sort((a, b) => {
      const rankDiff = groupRank(a.theme) - groupRank(b.theme);
      if (rankDiff !== 0) return rankDiff;
      return a.originalIndex - b.originalIndex;
    });
    return list.map((entry) => entry.theme);
  }, [themes, themeGroups, activeGroupId, favoriteThemeKeys]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (currentIndex >= carouselThemes.length) {
      setCurrentIndex(Math.max(0, carouselThemes.length - 1));
    }
  }, [carouselThemes.length, currentIndex]);

  useEffect(() => {
    if (activeGroupId === 'favorites' && favoriteThemeKeys.length === 0) {
      setActiveGroupId('all');
      setCurrentIndex(0);
    }
  }, [activeGroupId, favoriteThemeKeys.length]);

  useEffect(() => {
    restoredThemeSessionRef.current = false;
  }, [themeSessionStorageKey]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (restoredThemeSessionRef.current) return;
    if (themes.length === 0 || carouselThemes.length === 0) return;
    if (explicitFocusThemeKey) return;

    let savedState = null;
    try {
      savedState = JSON.parse(window.sessionStorage.getItem(themeSessionStorageKey) || 'null');
    } catch (error) {
      savedState = null;
    }
    if (!savedState) {
      restoredThemeSessionRef.current = true;
      return;
    }

    const allowedGroupIds = new Set([
      'all',
      'favorites',
      ...themeGroups.map((group) => group.id)
    ]);
    const savedGroupId = String(savedState.activeGroupId || '').trim();
    const savedThemeKey = String(savedState.themeKey || '').trim();

    if (savedGroupId && savedGroupId !== activeGroupId && allowedGroupIds.has(savedGroupId)) {
      setActiveGroupId(savedGroupId);
      return;
    }

    if (savedThemeKey) {
      const savedIndex = carouselThemes.findIndex((theme) => getThemeKey(theme) === savedThemeKey);
      if (savedIndex !== -1) {
        setCurrentIndex(savedIndex);
      }
    }
    restoredThemeSessionRef.current = true;
  }, [themeSessionStorageKey, themes, carouselThemes, activeGroupId, explicitFocusThemeKey, themeGroups]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!explicitFocusThemeKey) {
      appliedFocusThemeKeyRef.current = '';
      return;
    }
    if (carouselThemes.length === 0) return;
    if (appliedFocusThemeKeyRef.current === explicitFocusThemeKey) return;
    const focusIndex = carouselThemes.findIndex((theme) => getThemeKey(theme) === explicitFocusThemeKey);
    if (focusIndex !== -1) {
      setCurrentIndex(focusIndex);
      appliedFocusThemeKeyRef.current = explicitFocusThemeKey;
    }
  }, [explicitFocusThemeKey, carouselThemes]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentTheme = carouselThemes[currentIndex] || themes[currentIndex];
  const currentThemeSessionKey = currentTheme ? getThemeKey(currentTheme) : '';

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!currentThemeSessionKey) return;
    try {
      window.sessionStorage.setItem(themeSessionStorageKey, JSON.stringify({
        activeGroupId,
        themeKey: currentThemeSessionKey
      }));
    } catch (error) {
      // Ignore session-storage failures in restrictive environments.
    }
  }, [themeSessionStorageKey, activeGroupId, currentThemeSessionKey]);

  const formatMoney = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
      minimumFractionDigits: 0
    }).format(value);
  };

  // Reset image dot to 0 whenever the user slides to a different theme
  useEffect(() => {
    setModalImgIdx(0);
  }, [currentIndex]);

  useEffect(() => {
    setModalImgIdx(0);
  }, [showDetailsModal]);

  const themeImages = useMemo(() => {
    if (!currentTheme) return [];
    // Collect all image fields from the theme object
    const candidates = [];
    // Primary image
    if (currentTheme.image_url) candidates.push(currentTheme.image_url);
    if (currentTheme.image) candidates.push(currentTheme.image);
    if (currentTheme.curated_image_url) candidates.push(currentTheme.curated_image_url);
    // Moodboard images — may be an array, object, or null
    const mb = currentTheme.moodboard_images;
    if (mb) {
      if (Array.isArray(mb)) {
        mb.forEach((v) => { if (typeof v === 'string') candidates.push(v); });
      } else if (typeof mb === 'object') {
        Object.values(mb).forEach((v) => { if (typeof v === 'string') candidates.push(v); });
      }
    }
    // Also sweep any keys that look like image URLs on the theme itself
    ['image2', 'image3', 'image4', 'photo_url', 'hero_image'].forEach((k) => {
      if (typeof currentTheme[k] === 'string' && currentTheme[k].trim()) candidates.push(currentTheme[k]);
    });
    return Array.from(new Set(candidates.filter((img) => typeof img === 'string' && img.trim() !== '')));
  }, [currentTheme]);

  // Check if current theme is the selected one
  const isSelected = selectedTheme && (
    (currentTheme?.id && selectedTheme.id && currentTheme.id === selectedTheme.id) ||
    (currentTheme?.name && selectedTheme.name && currentTheme.name === selectedTheme.name)
  );
  const currentThemePlanKey = getThemePlanKey(currentTheme);
  useEffect(() => {
    if (currentThemePlanKey && onThemeFocusChange) {
      onThemeFocusChange(currentThemePlanKey);
    }
  }, [currentThemePlanKey, onThemeFocusChange]);
  const hasGeneratedPlanForTheme = Boolean(currentThemePlanKey && themePlans?.[currentThemePlanKey])
    || Boolean(isSelected && hasShoppingItems);

  // Contextual Action Logic
  const focusSelection = eventData?.focus_selection || [];
  // "Theme Only" if user selected ONLY "theme_ideas" (and maybe non-logistics like decoration)
  // but explicitly NOT logistics, food, venue
  const isThemeOnly = focusSelection.includes('theme_ideas') &&
    !focusSelection.includes('logistics') &&
    !focusSelection.includes('food') &&
    !focusSelection.includes('venue');

  const getActionLabel = () => {
    if (hasGeneratedPlanForTheme) return 'View Plan';
    return getIntentCtaLabel(eventData?.focus_selection, 'themeCard');
  };

  const canStartFreshThemeSet = !isAuthenticated || canGenerateFreshThemeSet(effectiveBillingUser);
  const canStartPlan = !isAuthenticated || canGenerateMatchingPlan(effectiveBillingUser);

  const getRequestedThemeCount = () => {
    if (!isAuthenticated) return 2;
    const requested = Number(eventData?.theme_count);
    return [2, 4].includes(requested) ? requested : 4;
  };

  const routeToFreshSetWorkflow = (extraData = {}) => {
    if (!onRegenerate) return;
    const returnThemeKey = currentThemePlanKey || currentTheme?.id || currentTheme?.name || '';
    const requestedCount = Number(extraData.theme_count);
    onRegenerate({
      ...eventData,
      ...extraData,
      theme_count: [2, 4].includes(requestedCount) ? requestedCount : getRequestedThemeCount(),
      __openFreshSetWorkflow: Boolean(extraData.__openFreshSetWorkflow),
      __returnToThemesAfterFreshSet: true,
      __returnThemeKey: returnThemeKey,
      __focusThemeKey: returnThemeKey,
      __returnThemeIndex: currentIndex,
      __freshSetRequestId: `${Date.now()}-${Math.random().toString(36).slice(2)}`
    });
  };

  const openFreshSetModal = (mode) => {
    setFreshSetCount(getRequestedThemeCount());
    setFreshSetMode('append');
    setFreshSetSummary({
      event_type: eventData?.event_type || '',
      guest_count: eventData?.guest_count ? String(eventData.guest_count) : '',
      budget: eventData?.budget ? String(eventData.budget) : '',
      location: eventData?.location || eventData?.zip_code || '',
      event_date: eventData?.event_date || eventData?.date || '',
      // Fresh mode does NOT carry a "theme direction" — it should regenerate
      // from raw user inputs, mirroring the original generation flow.
      theme_input: mode === 'similar'
        ? (currentTheme?.name || eventData?.theme_input || '')
        : '',
      additional_details: stripInternalThemeDirection(eventData?.additional_details || '')
    });
    setFreshSetModal(mode);
    setShowMoreMenu(false);
  };

  const buildSimilarThemeNoteFromSummary = () => {
    const seedName = freshSetSummary.theme_input.trim() || currentTheme?.name || 'the selected theme';
    const seedDescription = currentTheme?.description ? ` It worked because: ${currentTheme.description}` : '';
    const seedPalette = (currentTheme?.color_palette || [])
      .map((color) => typeof color === 'string' ? color : [color?.name, color?.hex].filter(Boolean).join(' '))
      .filter(Boolean)
      .slice(0, 6)
      .join(', ');
    const seedAnchors = (currentTheme?.sample_items || []).slice(0, 4).join(', ');
    const paletteCopy = seedPalette ? ` Stay close to this palette and mood: ${seedPalette}.` : '';
    const anchorCopy = seedAnchors ? ` Keep the practical anchors in this direction: ${seedAnchors}.` : '';
    const baseDetails = stripInternalThemeDirection(freshSetSummary.additional_details);
    const similarNote = `Similar-theme direction: create a fresh set tightly inspired by "${seedName}". Keep the same core era, formality, atmosphere, color language, and hosting style; vary only the execution angle. Do not pivot into a contrasting theme, unrelated art movement, or different party vibe.${seedDescription}${paletteCopy}${anchorCopy}`;
    return [baseDetails, similarNote].filter(Boolean).join('\n\n');
  };

  const startFreshSetFromModal = () => {
    const isSimilar = freshSetModal === 'similar';
    const shouldAppend = freshSetMode === 'append';
    const setId = shouldAppend ? `theme-set-${Date.now()}-${Math.random().toString(36).slice(2)}` : '';
    const parsedGuests = Number(freshSetSummary.guest_count);
    const parsedBudget = Number(freshSetSummary.budget);
    // Fresh mode = label as the next sequential set (e.g., "Set 2", "Set 3").
    // Similar mode = label as "Inspired by <theme>".
    let nextSetLabel = '';
    if (shouldAppend) {
      if (isSimilar) {
        nextSetLabel = `Inspired by ${freshSetSummary.theme_input.trim() || currentTheme?.name || 'your theme'}`;
      } else {
        // Count distinct non-original sets already present and add 2 (original = Set 1).
        const existingSetIds = new Set(
          (themes || [])
            .map((t) => t?.theme_set_id)
            .filter(Boolean)
        );
        nextSetLabel = `Set ${existingSetIds.size + 2}`;
      }
    }
    routeToFreshSetWorkflow({
      event_type: freshSetSummary.event_type.trim() || eventData?.event_type,
      guest_count: Number.isFinite(parsedGuests) && parsedGuests > 0 ? parsedGuests : eventData?.guest_count,
      budget: Number.isFinite(parsedBudget) && parsedBudget > 0 ? parsedBudget : eventData?.budget,
      location: freshSetSummary.location.trim() || eventData?.location,
      event_date: freshSetSummary.event_date || eventData?.event_date,
      date: freshSetSummary.event_date || eventData?.date,
      theme_count: freshSetCount,
      // Fresh mode: do NOT carry forward currentTheme.name as "theme direction".
      // Fresh generation should behave like original generation (user inputs only).
      theme_input: isSimilar
        ? (freshSetSummary.theme_input.trim() || currentTheme?.name || eventData?.theme_input || '')
        : (eventData?.theme_input || ''),
      additional_details: stripInternalThemeDirection(freshSetSummary.additional_details || eventData?.additional_details || ''),
      __aiAdditionalDetails: isSimilar ? buildSimilarThemeNoteFromSummary() : '',
      __appendThemes: shouldAppend,
      __similarToThemeKey: isSimilar ? (currentThemePlanKey || currentTheme?.id || currentTheme?.name || '') : '',
      __similarToThemeName: isSimilar ? (freshSetSummary.theme_input.trim() || currentTheme?.name || '') : '',
      __themeSetId: shouldAppend ? setId : '',
      __themeSetLabel: nextSetLabel,
      __openFreshSetWorkflow: false
    });
    setFreshSetModal(null);
  };

  const handleDeleteThemeGroup = (group) => {
    if (group.id === 'original') return;
    const keysToRemove = new Set(
      themes
        .filter((theme) => (theme?.theme_set_id || theme?.variant_of_theme_key || theme?.variant_of_theme_name) === group.id)
        .map((theme) => getThemeKey(theme))
    );
    // Also remove any favorites that belong to this group before deleting
    if (onToggleThemeFavorite) {
      keysToRemove.forEach((key) => {
        if (favoriteThemeKeys.includes(key)) {
          onToggleThemeFavorite(key);
        }
      });
    }
    const nextThemes = themes.filter((theme) => !keysToRemove.has(getThemeKey(theme)));
    if (nextThemes.length === themes.length) return;
    const nextIndex = Math.min(currentIndex, Math.max(0, nextThemes.length - 1));
    setCurrentIndex(nextIndex);
    // Reset group filter to 'all' since the active group is being removed
    if (activeGroupId === group.id) setActiveGroupId('all');
    if (onRegenerate) {
      onRegenerate({
        ...eventData,
        __replaceThemes: nextThemes,
        __focusThemeKey: getThemeKey(nextThemes[nextIndex]) || '',
        __returnThemeKey: getThemeKey(nextThemes[nextIndex]) || '',
        __deletedThemeKeys: [...(eventData?.__deletedThemeKeys || []), ...Array.from(keysToRemove)]
      });
    }
  };

  const handleDeleteCurrentTheme = () => {
    const keyToRemove = getThemeKey(currentTheme);
    if (!keyToRemove || themes.length <= 1) return;
    const nextThemes = themes.filter((theme) => getThemeKey(theme) !== keyToRemove);
    if (nextThemes.length === themes.length) return;
    const nextIndex = Math.min(currentIndex, Math.max(0, nextThemes.length - 1));
    setCurrentIndex(nextIndex);

    if (onToggleThemeFavorite && favoriteThemeKeys.includes(keyToRemove)) {
      onToggleThemeFavorite(keyToRemove);
    }

    if (onRegenerate) {
      onRegenerate({
        ...eventData,
        __replaceThemes: nextThemes,
        __focusThemeKey: getThemeKey(nextThemes[nextIndex]) || '',
        __returnThemeKey: getThemeKey(nextThemes[nextIndex]) || '',
        __deletedThemeKeys: [...(eventData?.__deletedThemeKeys || []), keyToRemove]
      });
    }
  };

  const openBillingForCurrentTheme = (intent = 'fresh_set', themeParam = null) => {
    if (!onBuyCredits) return;
    const activeTheme = themeParam || currentTheme;
    const activeThemePlanKey = getThemePlanKey(activeTheme);
    const returnThemeKey = activeThemePlanKey || activeTheme?.id || activeTheme?.name || '';
    onBuyCredits({
      screen: 'carousel',
      intent,
      returnThemeKey,
      eventData: {
        ...(eventData || {}),
        __focusThemeKey: returnThemeKey,
        __returnThemeKey: returnThemeKey
      }
    });
  };

  const handlePrevious = () => {
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => Math.min(carouselThemes.length - 1, prev + 1));
  };

  const getSlideWidth = () => carouselViewportRef.current?.offsetWidth || 320;

  const SWIPE_DOMINANCE_RATIO = 1.0;
  const touchStartXRef = useRef(null);
  const touchStartYRef = useRef(null);
  const [dragOffset, setDragOffset] = useState(0);

  const isInteractiveCardTarget = (target) => {
    return Boolean(
      target?.closest?.('button, a, input, textarea, select, [role="button"], [data-interactive="true"]')
    );
  };

  const handleCardTouchStart = (e) => {
    if (isInteractiveCardTarget(e.target)) {
      touchStartXRef.current = null;
      touchStartYRef.current = null;
      return;
    }
    const t = e.touches?.[0];
    if (!t) return;
    touchStartXRef.current = t.clientX;
    touchStartYRef.current = t.clientY;
  };
  const handleCardTouchMove = (e) => {
    const start = touchStartXRef.current;
    const startY = touchStartYRef.current;
    if (start == null || startY == null) return;
    const t = e.touches?.[0];
    if (!t) return;
    const dx = t.clientX - start;
    const dy = t.clientY - startY;
    // Only follow the finger when motion is mostly horizontal — otherwise
    // leave page scroll alone.
    if (Math.abs(dx) < Math.abs(dy)) return;
    e.preventDefault();
    setIsCarouselDragging(true);
    const slideWidth = getSlideWidth();
    const atStart = currentIndex === 0 && dx > 0;
    const atEnd = currentIndex === carouselThemes.length - 1 && dx < 0;
    const damped = (atStart || atEnd) ? dx * 0.25 : dx;
    const maxDrag = slideWidth;
    setDragOffset(Math.max(-maxDrag, Math.min(maxDrag, damped)));
  };
  const handleCardTouchEnd = (e) => {
    const start = touchStartXRef.current;
    const startY = touchStartYRef.current;
    touchStartXRef.current = null;
    touchStartYRef.current = null;
    setIsCarouselDragging(false);
    if (start == null || startY == null) return;
    const t = e.changedTouches?.[0];
    if (!t) return;
    const dx = t.clientX - start;
    const dy = t.clientY - startY;
    const slideWidth = getSlideWidth();
    const threshold = Math.min(40, slideWidth * 0.12);
    if (Math.abs(dx) < threshold || Math.abs(dx) < Math.abs(dy) * SWIPE_DOMINANCE_RATIO) {
      setDragOffset(0);
      return;
    }
    setDragOffset(0);
    if (dx < 0 && currentIndex < carouselThemes.length - 1) {
      handleNext();
    } else if (dx > 0 && currentIndex > 0) {
      handlePrevious();
    }
  };

  // Pointer-event based drag (trackpad / mouse drag). Works alongside touch.
  const pointerStartRef = useRef({ id: null, x: 0, y: 0, isDragging: false });
  const wheelSwipeCooldownRef = useRef(0);
  const handleCardPointerDown = (e) => {
    // Only react to primary mouse / pen / trackpad input — not touch (handled above).
    if (e.pointerType === 'touch') return;
    if (e.button !== undefined && e.button !== 0) return;
    // Ignore drags that start on interactive elements (buttons, links, inputs).
    const tgt = e.target;
    if (tgt && tgt.closest && tgt.closest('button, a, input, textarea, select, [role="button"]')) {
      return;
    }
    pointerStartRef.current = {
      id: e.pointerId,
      x: e.clientX,
      y: e.clientY,
      isDragging: true
    };
  };
  const handleCardPointerMove = (e) => {
    const start = pointerStartRef.current;
    if (!start.isDragging || start.id !== e.pointerId) return;
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    if (Math.abs(dx) < Math.abs(dy)) return;
    setIsCarouselDragging(true);
    const slideWidth = getSlideWidth();
    const atStart = currentIndex === 0 && dx > 0;
    const atEnd = currentIndex === carouselThemes.length - 1 && dx < 0;
    const damped = (atStart || atEnd) ? dx * 0.25 : dx;
    const maxDrag = slideWidth;
    setDragOffset(Math.max(-maxDrag, Math.min(maxDrag, damped)));
  };
  const handleCardPointerUp = (e) => {
    const start = pointerStartRef.current;
    if (!start.isDragging || start.id !== e.pointerId) return;
    pointerStartRef.current = { id: null, x: 0, y: 0, isDragging: false };
    setIsCarouselDragging(false);
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    const slideWidth = getSlideWidth();
    const threshold = Math.min(40, slideWidth * 0.12);
    if (Math.abs(dx) < threshold || Math.abs(dx) < Math.abs(dy) * SWIPE_DOMINANCE_RATIO) {
      setDragOffset(0);
      return;
    }
    setDragOffset(0);
    if (dx < 0 && currentIndex < carouselThemes.length - 1) {
      handleNext();
    } else if (dx > 0 && currentIndex > 0) {
      handlePrevious();
    }
  };
  const handleCardPointerCancel = () => {
    pointerStartRef.current = { id: null, x: 0, y: 0, isDragging: false };
    setIsCarouselDragging(false);
    setDragOffset(0);
  };

  const handleCarouselWheel = (e) => {
    if (carouselThemes.length <= 1) return;
    const absX = Math.abs(e.deltaX);
    const absY = Math.abs(e.deltaY);
    if (absX < 24 || absX <= absY) return;

    e.preventDefault();

    const now = Date.now();
    if (now - wheelSwipeCooldownRef.current < 280) return;
    wheelSwipeCooldownRef.current = now;

    if (e.deltaX > 0 && currentIndex < carouselThemes.length - 1) {
      handleNext();
    } else if (e.deltaX < 0 && currentIndex > 0) {
      handlePrevious();
    }
  };

  const modalTouchStartRef = useRef({ x: null, y: null });
  const [modalDragOffset, setModalDragOffset] = useState(0);
  const [isModalDragging, setIsModalDragging] = useState(false);
  const handleModalTouchStart = (e) => {
    const t = e.touches?.[0];
    if (!t) return;
    modalTouchStartRef.current = { x: t.clientX, y: t.clientY };
    setIsModalDragging(true);
    setModalDragOffset(0);
  };
  const handleModalTouchMove = (e) => {
    const start = modalTouchStartRef.current;
    const t = e.touches?.[0];
    if (!t || start.x == null || start.y == null) return;
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    if (Math.abs(dx) < Math.abs(dy)) return;
    const damped =
      (currentIndex === 0 && dx > 0) || (currentIndex === carouselThemes.length - 1 && dx < 0)
        ? dx * 0.25
        : dx;
    const maxDrag = 96;
    setModalDragOffset(Math.max(-maxDrag, Math.min(maxDrag, damped)));
  };
  const handleModalTouchEnd = (e) => {
    const start = modalTouchStartRef.current;
    modalTouchStartRef.current = { x: null, y: null };
    setIsModalDragging(false);
    if (start.x == null || start.y == null) return;
    const t = e.changedTouches?.[0];
    if (!t) return;
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    if (Math.abs(dx) < 56 || Math.abs(dx) < Math.abs(dy) * 1.2) {
      setModalDragOffset(0);
      return;
    }
    setModalDragOffset(0);
    if (dx < 0 && currentIndex < carouselThemes.length - 1) {
      handleNext();
    } else if (dx > 0 && currentIndex > 0) {
      handlePrevious();
    }
  };
  const handleModalTouchCancel = () => {
    modalTouchStartRef.current = { x: null, y: null };
    setIsModalDragging(false);
    setModalDragOffset(0);
  };

  const carouselTrackStyle = carouselThemes.length > 0 && !isGridMode ? {
    width: `${carouselThemes.length * 100}%`,
    transform: `translateX(calc(-${currentIndex * (100 / carouselThemes.length)}% + ${dragOffset}px))`,
    transition: isCarouselDragging || dragOffset ? 'none' : 'transform 220ms cubic-bezier(0.16, 1, 0.3, 1)'
  } : {
    width: '100%',
    transform: 'none',
    transition: 'none'
  };

  const openConfirmation = (themeParam = null) => {
    // Demo restriction: No ability to plan the event
    if (!isAuthenticated) {
      const focusSelection = eventData?.focus_selection || [];
      const isFoodOnly = focusSelection.length === 1 && focusSelection[0] === 'food';
      setConfirmDialog({
        title: isFoodOnly ? 'Sign up to plan this menu' : 'Sign up to plan this event',
        message: isFoodOnly
          ? 'Demo mode lets you browse menu concepts, but a full food & drinks plan and shopping list require a free account. Signing up also unlocks 4-concept generation and saved progress.'
          : 'Demo mode lets you browse theme ideas, but a full event plan and shopping list require a free account. Signing up also unlocks 4-theme generation and saved progress.',
        confirmText: 'Sign up free',
        cancelText: 'Not now',
        onConfirm: () => onRequestAuth?.('signup')
      });
      return;
    }

    if (!canStartPlan) {
      const activeTheme = themeParam || currentTheme;
      openBillingForCurrentTheme('plan_theme', activeTheme);
      return;
    }

    setShowConfirmation(true);
  };


  const handleConfirmSelection = (preferences) => {
    if (onUpdateSelectionPreferences) {
      onUpdateSelectionPreferences(preferences);
    }
    setShowConfirmation(false);
    onThemeSelect(currentTheme, preferences);
  };

  const handleRegenerate = () => {
    const focusSelection = eventData?.focus_selection || [];
    // Warn if user has shopping items
    if (hasShoppingItems) {
      setConfirmDialog({
        title: `Generate fresh ${getConceptLabelForIntent(focusSelection, 'concepts-lower')}?`,
        message: `You already have a plan/shopping list. You can keep your existing ${getConceptLabelForIntent(focusSelection, 'concepts-lower')} and add the new set, or replace the current ${getConceptLabelForIntent(focusSelection, 'concept-lower')} set. Existing plans stay available unless you plan another ${getConceptLabelForIntent(focusSelection, 'concept-lower')}.`,
        confirmText: 'Choose options',
        cancelText: 'Not now',
        onConfirm: () => openFreshSetModal('fresh')
      });
      return;
    }

    if (!isAuthenticated) {
      setConfirmDialog({
        title: `Save before fresh ${getConceptLabelForIntent(focusSelection, 'concepts-lower')}?`,
        message: `Fresh ${getConceptLabelForIntent(focusSelection, 'concepts-lower')} in demo mode may not be saved if you leave. Sign up to save this event and unlock 4-${getConceptLabelForIntent(focusSelection, 'concept-lower')}-generation.`,
        confirmText: 'Sign up',
        cancelText: 'Continue demo',
        onConfirm: () => onRequestAuth?.('signup'),
        onCancel: () => openFreshSetModal('fresh')
      });
      return;
    }

    if (isAuthenticated && !canStartFreshThemeSet) {
      openBillingForCurrentTheme('fresh_set');
      return;
    }

    openFreshSetModal('fresh');
  };

  const handleGenerateSimilarThemes = () => {
    const focusSelection = eventData?.focus_selection || [];
    if (!isAuthenticated) {
      setConfirmDialog({
        title: `Save similar ${getConceptLabelForIntent(focusSelection, 'concepts-lower')}?`,
        message: `Similar ${getConceptLabelForIntent(focusSelection, 'concepts-lower')} may not be saved if you leave. Sign up to keep this event, save variants, and unlock the full planning flow.`,
        confirmText: 'Sign up',
        cancelText: 'Continue demo',
        onConfirm: () => onRequestAuth?.('signup'),
        onCancel: () => openFreshSetModal('similar')
      });
      return;
    }

    if (isAuthenticated && !canStartFreshThemeSet) {
      openBillingForCurrentTheme('similar_themes');
      return;
    }

    openFreshSetModal('similar');
  };

  // Preload images for ALL themes eagerly when data arrives — eliminates
  // the 2-5s lag when sliding to a new theme card
  useEffect(() => {
    if (!themes || themes.length === 0) return;
    themes.forEach(theme => {
      const themeHeroImage = theme?.image_url || theme?.image || theme?.curated_image_url;
      if (themeHeroImage) {
        const img = new window.Image();
        img.src = themeHeroImage;
      }
    });
  }, [themes]);

  // Guard: if themes is empty or the current entry is missing, show a graceful empty state
  const favoriteCount = favoriteThemeKeys.length;
  const showFavoritesOnly = activeGroupId === 'favorites';

  if (!themes || themes.length === 0) {
    return (
      <div className={`max-w-6xl mx-auto px-4 pt-10 sm:pt-14 md:pt-16 flex flex-col items-center justify-center py-24 text-center`}>
        <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mb-6">
          <svg className="w-10 h-10 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
        </div>
        <h3 className="text-2xl font-bold text-gray-800 mb-2">No Themes Available</h3>
        <p className="text-gray-500 mb-6 max-w-sm">Themes for this plan haven't been generated yet, or couldn't be loaded.</p>
        {onBackToInput && (
          <button
            onClick={onBackToInput}
            className="px-6 py-3 bg-primary text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition"
          >
            ← Back to Details
          </button>
        )}
      </div>
    );
  }

  if (showFavoritesOnly && carouselThemes.length === 0) {
    return (
      <div className="max-w-6xl mx-auto px-4 pt-10 sm:pt-14 md:pt-16 flex flex-col items-center justify-center py-24 text-center">
        <h3 className="text-2xl font-bold text-gray-800 mb-2">No favorite themes yet</h3>
        <p className="text-gray-500 mb-6 max-w-sm">Star themes you like, then use the favorites filter to jump between them quickly.</p>
        <button
          type="button"
          onClick={() => setActiveGroupId('all')}
          className="px-6 py-3 bg-primary text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition"
        >
          Show all themes
        </button>
      </div>
    );
  }

  if (!currentTheme) return null;

  const jumpToGroup = (group) => {
    setActiveGroupId(group.id);
    setCurrentIndex(0);
    setDragOffset(0);
    touchStartXRef.current = null;
    touchStartYRef.current = null;
    setIsCarouselDragging(false);
  };

  return (
    <div className={`max-w-6xl mx-auto px-2 pt-1 sm:px-4 sm:pt-6 md:pt-10 relative bg-gray-50`} style={{ zIndex: 10, isolation: 'isolate', position: 'relative' }}>
      
      {/* Centered column for premium presentation */}
      <div className="w-full sm:max-w-3xl lg:max-w-5xl mx-auto flex flex-col items-center">
        
        {/* Horizontal scrollable pill tab bar — single row, no wrapping */}
        {(themeGroups.length > 1 || favoriteCount > 0) && (
          <div className="w-full mb-1.5 sm:mb-5 font-sans shrink-0">
            <div
              className="flex flex-nowrap gap-2 items-center px-1 py-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              style={{ scrollSnapType: 'x mandatory' }}
            >
              {/* "All" pill — always first */}
              <button
                ref={activeGroupId === 'all' ? activePillRef : null}
                type="button"
                onClick={() => setActiveGroupId('all')}
                className={`flex-none flex items-center gap-1 px-3.5 py-2 rounded-xl text-xs font-bold transition-all duration-200 whitespace-nowrap ${
                  activeGroupId === 'all'
                    ? 'bg-primary text-white shadow-md'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200 shadow-sm'
                }`}
              >
                All
                <span className="text-[10px] font-semibold opacity-75 ml-0.5">({themes.length})</span>
              </button>

              {/* Favorites pill */}
              {favoriteCount > 0 && (
                <button
                  ref={activeGroupId === 'favorites' ? activePillRef : null}
                  type="button"
                  onClick={() => setActiveGroupId('favorites')}
                  className={`flex-none flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-bold transition-all duration-200 whitespace-nowrap ${
                    activeGroupId === 'favorites'
                      ? 'bg-amber-500 text-white shadow-md'
                      : 'bg-white text-amber-600 hover:bg-amber-50 border border-gray-200 shadow-sm'
                  }`}
                >
                  <span>★</span>
                  <span>Favorites ({favoriteCount})</span>
                </button>
              )}

              {/* Group pills */}
              {themeGroups.map((group) => {
                const isActive = activeGroupId === group.id;
                const canDelete = group.id !== 'original';
                return (
                  <div
                    key={group.id}
                    ref={isActive ? activePillRef : null}
                    className={`flex-none relative flex items-center rounded-xl transition-all duration-200 whitespace-nowrap ${
                      isActive
                        ? 'bg-primary text-white shadow-md'
                        : 'bg-white hover:bg-gray-50 border border-gray-200 shadow-sm'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => jumpToGroup(group)}
                      className={`flex items-center gap-1 px-3.5 py-2 text-xs font-bold transition-colors ${
                        isActive ? 'text-white' : 'text-gray-700 hover:text-primary'
                      } ${canDelete ? 'pr-8' : ''}`}
                    >
                      <span>{group.label}</span>
                      <span className="text-[10px] font-semibold opacity-75 ml-0.5">({group.count})</span>
                    </button>

                    {canDelete && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setPendingDeleteGroup(group);
                        }}
                        className={`absolute right-1 top-1/2 -translate-y-1/2 inline-flex h-5 w-5 items-center justify-center rounded-full transition-colors ${
                          isActive
                            ? 'text-white/80 hover:bg-white/20 hover:text-white'
                            : 'text-gray-400 hover:bg-gray-200 hover:text-gray-700'
                        }`}
                        title={`Remove ${group.label}`}
                      >
                        <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Main Carousel Column */}
        <div className="w-full flex flex-col items-center">
          {/* Viewport for Carousel */}
          <div
            ref={carouselViewportRef}
            className={`w-full ${isGridMode ? 'max-w-none overflow-visible' : 'max-w-2xl overflow-hidden relative touch-pan-y pointer-events-auto cursor-grab active:cursor-grabbing'}`}
            style={isGridMode ? {} : { overscrollBehaviorX: 'contain' }}
            onTouchStart={isGridMode ? null : handleCardTouchStart}
            onTouchMove={isGridMode ? null : handleCardTouchMove}
            onTouchEnd={isGridMode ? null : handleCardTouchEnd}
            onPointerDown={isGridMode ? null : handleCardPointerDown}
            onPointerMove={isGridMode ? null : handleCardPointerMove}
            onPointerUp={isGridMode ? null : handleCardPointerUp}
            onPointerCancel={isGridMode ? null : handleCardPointerCancel}
            onWheel={isGridMode ? null : handleCarouselWheel}
          >
            <div className={isGridMode ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 w-full" : "flex"} style={carouselTrackStyle}>
              {carouselThemes.map((slideTheme, slideIndex) => {
                const paletteColors = (slideTheme?.color_palette || []).map(c =>
                  typeof c === 'string' ? c : c?.hex || '#e5e7eb'
                );
                const sampleItems = slideTheme?.sample_items || slideTheme?.plan_anchors || [];
                const hasGeneratedPlanForTheme = themePlans && themePlans[getThemePlanKey(slideTheme)];
                const scoreText = parseAndFormatScore(slideTheme?.critic_score);
                const firstPlannerBadge = getFirstPlannerBadge(slideTheme);

                return (
                  <div
                    key={getThemeKey(slideTheme) || slideIndex}
                    className={isGridMode ? "w-full animate-fade-in" : "px-1.5 sm:px-3 flex-shrink-0"}
                    style={isGridMode ? {} : {
                      width: `${100 / carouselThemes.length}%`,
                    }}
                  >
                    <div className={`bg-white rounded-2xl sm:rounded-3xl shadow-xl border border-gray-200 overflow-hidden flex flex-col select-none relative ${isGridMode ? 'h-full' : 'h-auto md:h-[440px]'}`}>
                      
                      {/* Palette colored top bar (Homepage Inspired) */}
                      <div className="h-1.5 w-full shrink-0" style={{ background: `linear-gradient(90deg, ${paletteColors.join(', ')})` }} />

                      {/* Cover Image Preview */}
                      {slideTheme?.image_url && (
                        <div className="w-full aspect-[21/9] sm:aspect-video bg-gray-100 overflow-hidden shrink-0 relative border-b border-gray-100">
                          <img
                            src={slideTheme.image_url}
                            alt={slideTheme.name || 'Theme Preview'}
                            className="w-full h-full object-cover pointer-events-none select-none"
                            loading="lazy"
                          />
                        </div>
                      )}

                      {/* Card Header (Homepage Inspired category/guest badges) */}
                      <div className="px-4 py-2.5 sm:px-5 sm:pt-4 sm:pb-3 border-b border-gray-100 flex flex-col gap-2 shrink-0">
                        {/* Primary Row: Event Type Chip & Planner Badge / Fit Score Badge */}
                        <div className="flex items-start justify-between gap-2 w-full min-w-0">
                          <div className="flex flex-wrap items-center gap-1 min-w-0">
                            <span className="text-[11px] font-bold px-2.5 py-0.5 rounded-full bg-stone-100 text-stone-700 uppercase tracking-wide whitespace-normal break-words max-w-full" title={eventData?.event_type || 'Party'}>
                              {eventData?.event_type || 'Party'}
                            </span>
                            {firstPlannerBadge && (
                              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100/60 uppercase tracking-wide whitespace-normal break-words max-w-full" title={firstPlannerBadge}>
                                {firstPlannerBadge}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1.5 shrink-0">
                            {scoreText && (
                              <span className="text-[11.5px] font-extrabold px-2 py-0.5 rounded-full bg-emerald-600 text-white flex items-center gap-0.5 shadow-sm shrink-0">
                                <span>★ {scoreText}</span>
                                <span className="hidden sm:inline">Fit</span>
                              </span>
                            )}
                            <span className="sm:hidden text-[11px] font-bold text-gray-500 tabular-nums shrink-0">
                              {slideIndex + 1}/{carouselThemes.length}
                            </span>
                          </div>
                        </div>

                        {/* Secondary Row: Guest count & Budget */}
                        <div className="text-[11.5px] text-gray-500 font-semibold font-sans flex items-center gap-1.5 flex-wrap min-w-0">
                          <svg className="w-3.5 h-3.5 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                          <span className="whitespace-normal break-words">{eventData?.guest_count || 10} guests</span>
                          <span className="text-gray-300 shrink-0">•</span>
                          <svg className="w-3.5 h-3.5 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M12 14a2 2 0 110-4h4a2 2 0 100 4h-4z" />
                          </svg>
                          <span className="whitespace-normal break-words">{formatMoney(eventData?.budget || 0)}</span>
                        </div>
                      </div>

                      {/* Card Body (Title, description, palette swatches row, checklist preview) */}
                      <div className="p-3 sm:p-5 flex-grow flex flex-col min-h-0 sm:min-h-[220px]">
                        
                        {/* Title + Actions Row */}
                        <div className="flex items-start justify-between gap-3 sm:gap-4 mb-1.5 sm:mb-3 shrink-0">
                          <h3 className="font-extrabold text-base sm:text-lg text-gray-900 leading-tight font-sans">
                            {slideTheme?.name || 'Untitled Theme'}
                          </h3>
                          <div className="flex items-center gap-1 shrink-0">
                            {onToggleThemeFavorite && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onToggleThemeFavorite(getThemeKey(slideTheme));
                                }}
                                className={`p-1 rounded-xl transition-all shrink-0 ${
                                  favoriteThemeKeys.includes(getThemeKey(slideTheme))
                                    ? 'text-amber-500 hover:text-amber-600 scale-110'
                                    : 'text-gray-300 hover:text-amber-400 hover:scale-110'
                                  }`}
                                title={favoriteThemeKeys.includes(getThemeKey(slideTheme)) ? "Remove from Favorites" : "Add to Favorites"}
                              >
                                <svg className="w-5.5 h-5.5 fill-current" viewBox="0 0 24 24" style={{ width: 22, height: 22 }}>
                                  <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                                </svg>
                              </button>
                            )}
                            {onEditTheme && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onEditTheme(slideTheme);
                                }}
                                className="p-1 text-gray-400 hover:text-primary rounded-lg transition-colors shrink-0"
                                title={`Edit ${getConceptLabelForIntent(eventData?.focus_selection, 'concept')}`}
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                </svg>
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Theme Description */}
                        <p className="text-xs text-gray-600 leading-relaxed mb-1.5 sm:mb-4 flex-grow-0 font-sans whitespace-normal break-words">
                          {slideTheme?.description}
                        </p>

                        {/* Swatches Row — tap/click a swatch to see its name */}
                        {(() => {
                          const palette = (slideTheme?.color_palette || []).slice(0, 6);
                          if (!palette.length) return null;
                          const swatchStateKey = `swatch-${getThemeKey(slideTheme) || slideIndex}`;
                          return (
                            <SwatchRow key={swatchStateKey} palette={palette} />
                          );
                        })()}

                        {/* Checklist Preview (from the homepage design) */}
                        {sampleItems.length > 0 && (
                          <div className="space-y-1.5 border-t border-gray-100 pt-1.5 sm:space-y-2 sm:pt-4 flex-grow flex flex-col justify-end">
                            <span className="block text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1 font-sans">
                              SHOPPING LIST
                            </span>
                            <div className="space-y-2">
                              {sampleItems.slice(0, 3).map((item, idx) => (
                                <div key={idx} className="flex items-start gap-2.5 text-xs text-gray-700 font-semibold leading-tight font-sans">
                                  <span className="inline-flex items-center justify-center h-4.5 w-4.5 rounded-full bg-stone-100 text-zinc-700 shrink-0 mt-0.5">
                                    <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                    </svg>
                                  </span>
                                  <span className="min-w-0 break-words whitespace-normal">{item}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Action Footer (Build Plan CTA Hero + Secondary Details & More Menu) */}
                      <div className="p-3 sm:p-4 border-t border-stone-200/80 bg-stone-50/70 flex flex-col gap-2 shrink-0">
                        {/* Primary Hero Button */}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setCurrentIndex(slideIndex);
                            if (hasGeneratedPlanForTheme) {
                              onViewThemePlan?.(getThemePlanKey(slideTheme));
                            } else {
                              openConfirmation(slideTheme);
                            }
                          }}
                          className={`w-full h-10 sm:h-11 text-xs sm:text-sm font-bold rounded-xl shadow-sm active:scale-[0.98] transition-all font-sans flex items-center justify-center gap-1.5 px-3 ${
                            hasGeneratedPlanForTheme
                              ? 'bg-emerald-700 hover:bg-emerald-800 text-white'
                              : 'bg-stone-900 hover:bg-stone-800 text-white'
                          }`}
                        >
                          {hasGeneratedPlanForTheme ? (
                            <span>View Full Plan →</span>
                          ) : (
                            <span>Select & Build Plan →</span>
                          )}
                        </button>

                        {/* Secondary Actions Row */}
                        <div className="flex items-center justify-between gap-2">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setCurrentIndex(slideIndex);
                              setShowDetailsModal(true);
                            }}
                            className="flex-1 h-8 sm:h-8.5 border border-stone-300 bg-white hover:bg-stone-100 text-stone-700 hover:text-stone-900 text-xs font-semibold rounded-lg active:scale-[0.98] transition-all font-sans shadow-2xs flex items-center justify-center gap-1"
                          >
                            <span>Theme Details</span>
                          </button>

                          <div
                            ref={showMoreMenu && currentIndex === slideIndex ? moreMenuRef : null}
                            className="relative shrink-0"
                          >
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setCurrentIndex(slideIndex);
                                setShowMoreMenu(!showMoreMenu);
                              }}
                              className="h-8 w-8 sm:h-8.5 sm:w-8.5 flex items-center justify-center bg-white hover:bg-stone-100 border border-stone-300 rounded-lg active:scale-[0.98] transition-all text-stone-600 shadow-2xs"
                              aria-label="More options"
                            >
                              <svg className="w-3.5 h-3.5 text-stone-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                              </svg>
                            </button>

                          {showMoreMenu && currentIndex === slideIndex && (
                            <div className="absolute right-0 bottom-full mb-2 w-52 bg-white rounded-xl shadow-xl border border-gray-200 py-1.5 z-50">
                              <button
                                type="button"
                                onClick={() => {
                                  handleRegenerate();
                                  setShowMoreMenu(false);
                                }}
                                className="w-full text-left px-4 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 flex items-center justify-between transition font-sans"
                              >
                                <span>Generate Fresh {getConceptLabelForIntent(eventData?.focus_selection, 'concepts')}</span>
                                <span className="text-[10px] text-amber-500">✨</span>
                              </button>

                              <button
                                type="button"
                                onClick={() => {
                                  handleGenerateSimilarThemes();
                                  setShowMoreMenu(false);
                                }}
                                className="w-full text-left px-4 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 flex items-center justify-between transition font-sans"
                              >
                                <span>More Like This {getConceptLabelForIntent(eventData?.focus_selection, 'concept')}</span>
                                <svg className="h-4 w-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M4 12h7m0 0c4 0 4-6 9-6m-9 6c4 0 4 6 9 6" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M17 3l3 3-3 3M17 15l3 3-3 3" />
                                </svg>
                              </button>

                              {themes.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setConfirmDialog({
                                      title: `Remove this ${getConceptLabelForIntent(eventData?.focus_selection, 'concept-lower')}?`,
                                      message: `This removes the ${getConceptLabelForIntent(eventData?.focus_selection, 'concept-lower')} card from this event. Any generated plan for it stays saved unless you delete the plan separately.`,
                                      confirmText: `Remove ${getConceptLabelForIntent(eventData?.focus_selection, 'concept-lower')}`,
                                      cancelText: `Keep ${getConceptLabelForIntent(eventData?.focus_selection, 'concept-lower')}`,
                                      variant: 'danger',
                                      onConfirm: handleDeleteCurrentTheme
                                    });
                                    setShowMoreMenu(false);
                                  }}
                                  className="w-full text-left px-4 py-2 text-xs font-semibold text-red-600 hover:bg-red-50 flex items-center justify-between transition font-sans"
                                >
                                  <span>Remove This {getConceptLabelForIntent(eventData?.focus_selection, 'concept')}</span>
                                  <svg className="h-4 w-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M6 7h12M10 7V5.5A1.5 1.5 0 0111.5 4h1A1.5 1.5 0 0114 5.5V7m-6 0l.7 12A2 2 0 0010.7 21h2.6a2 2 0 002-2L16 7M10 11v6M14 11v6" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
              })}
            </div>
          </div>

          {/* Clean navigation controls below card for all screen sizes */}
          {carouselThemes.length > 1 && !isGridMode && (
            <div className="mt-4 flex items-center justify-center gap-3 sm:gap-4 shrink-0 font-sans pb-3">
              <button
                type="button"
                onClick={handlePrevious}
                disabled={currentIndex === 0}
                className="inline-flex h-9 w-9 sm:h-10 sm:w-10 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-700 shadow-sm transition hover:bg-gray-50 hover:border-stone-400 active:scale-95 disabled:cursor-not-allowed disabled:opacity-30"
                aria-label="Previous theme"
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              <div className="px-3.5 py-1.5 bg-white border border-gray-200 rounded-full shadow-sm text-xs font-bold text-gray-600 tabular-nums">
                {currentIndex + 1} of {carouselThemes.length}
              </div>

              <button
                type="button"
                onClick={handleNext}
                disabled={currentIndex === carouselThemes.length - 1}
                className="inline-flex h-9 w-9 sm:h-10 sm:w-10 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-700 shadow-sm transition hover:bg-gray-50 hover:border-stone-400 active:scale-95 disabled:cursor-not-allowed disabled:opacity-30"
                aria-label="Next theme"
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>
      {freshSetModal && typeof document !== 'undefined' && createPortal(
        <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-slate-950/50 p-4 backdrop-blur-sm sm:p-6"
          onClick={() => setFreshSetModal(null)}
        >
          <div
            className="flex max-h-[min(92vh,820px)] w-full max-w-xl flex-col overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-5 py-5 sm:px-6">
              <div>
                <h3 className="text-xl font-extrabold tracking-tight text-gray-950">
                  {freshSetModal === 'similar' ? `Generate more like this ${getConceptLabelForIntent(eventData?.focus_selection, 'concept-lower')}` : `Generate a fresh ${getConceptLabelForIntent(eventData?.focus_selection, 'concept-lower')} set`}
                </h3>
                <p className="mt-2 text-sm leading-6 text-gray-600">
                  {freshSetModal === 'similar'
                    ? `Create a new set inspired by "${currentTheme?.name || 'this theme'}" while keeping the current ${getConceptLabelForIntent(eventData?.focus_selection, 'concepts-lower')} available.`
                    : `Create a new set from your current event details. Your existing ${getConceptLabelForIntent(eventData?.focus_selection, 'concepts-lower')} stay available until the new set is ready.`}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setFreshSetModal(null)}
                className="inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-full border border-gray-200 bg-white px-3 text-sm font-bold text-gray-600 transition hover:bg-gray-50 hover:text-gray-950"
              >
                <span aria-hidden="true">x</span>
                Close
              </button>
            </div>

            <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-5 sm:px-6">
              <div className="rounded-2xl border-2 border-primary/30 bg-primary/5 p-4">
                <p className="mb-2 text-xs font-bold uppercase tracking-[0.16em] text-primary">What should happen to existing {getConceptLabelForIntent(eventData?.focus_selection, 'concepts-lower')}?</p>
                <div className="grid gap-3 sm:grid-cols-2">
                  {[
                    {
                      id: 'append',
                      title: freshSetModal === 'similar' ? 'Add as new variant set' : 'Keep existing',
                      body: freshSetModal === 'similar'
                        ? `Add a new variant set alongside your current ${getConceptLabelForIntent(eventData?.focus_selection, 'concepts-lower')}.`
                        : `Add a new set alongside your current ${getConceptLabelForIntent(eventData?.focus_selection, 'concepts-lower')}.`
                    },
                    {
                      id: 'replace',
                      title: freshSetModal === 'similar' ? 'Replace current set' : 'Start fresh',
                      body: `Replace the current ${getConceptLabelForIntent(eventData?.focus_selection, 'concepts-lower')} with the new set.`
                    }
                  ].map((option) => {
                    const isActive = freshSetMode === option.id;
                    return (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => setFreshSetMode(option.id)}
                        className={`rounded-2xl border p-3 text-left transition ${
                          isActive
                            ? 'border-primary bg-white text-primary shadow-sm ring-2 ring-primary'
                            : 'border-gray-200 bg-white text-gray-700 hover:border-primary/40 hover:bg-primary/5'
                        }`}
                      >
                        <span className="block text-sm font-extrabold">{option.title}</span>
                        <span className="mt-1 block text-xs leading-5 text-gray-500">{option.body}</span>
                      </button>
                    );
                  })}
                </div>
                <p className="mt-2 text-[11px] leading-4 text-gray-500">
                  Tip: you can delete any older set later from the set pills above the theme card.
                </p>
              </div>

              <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-gray-500">Event summary</p>
                  {onBackToInput && (
                    <button
                      type="button"
                      onClick={() => {
                        setFreshSetModal(null);
                        onBackToInput();
                      }}
                      className="rounded-xl border border-gray-200 bg-white px-3 py-1.5 text-xs font-bold text-gray-700 transition hover:bg-gray-50"
                    >
                      Full details
                    </button>
                  )}
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  {freshSetModal === 'similar' ? (
                    <div className="block min-w-0">
                      <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Event type</span>
                      <p className="mt-1 rounded-xl border border-gray-200 bg-white px-3 py-3 text-sm font-semibold text-gray-900">
                        {freshSetSummary.event_type || eventData?.event_type || '—'}
                      </p>
                    </div>
                  ) : (
                    <label className="block min-w-0">
                      <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Event type</span>
                      <input
                        type="text"
                        value={freshSetSummary.event_type}
                        onChange={(e) => setFreshSetSummary((prev) => ({ ...prev, event_type: e.target.value }))}
                        className="mt-1 h-12 w-full min-w-0 rounded-xl border border-gray-200 bg-white px-3 text-sm font-semibold text-gray-900 outline-none focus:border-primary"
                      />
                    </label>
                  )}
                  <label className="block min-w-0">
                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Guests</span>
                    <div className="relative mt-1">
                      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-gray-400" aria-hidden="true">👥</span>
                      <input
                        type="number"
                        min="1"
                        value={freshSetSummary.guest_count}
                        onChange={(e) => setFreshSetSummary((prev) => ({ ...prev, guest_count: e.target.value }))}
                        className="h-12 w-full min-w-0 rounded-xl border border-gray-200 bg-white pl-10 pr-3 text-sm font-semibold text-gray-900 outline-none focus:border-primary"
                      />
                    </div>
                  </label>
                  <label className="block min-w-0">
                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Budget</span>
                    <div className="relative mt-1">
                      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base font-bold text-gray-400" aria-hidden="true">$</span>
                      <input
                        type="number"
                        min="0"
                        value={freshSetSummary.budget}
                        onChange={(e) => setFreshSetSummary((prev) => ({ ...prev, budget: e.target.value }))}
                        className="h-12 w-full min-w-0 rounded-xl border border-gray-200 bg-white pl-8 pr-3 text-sm font-semibold text-gray-900 outline-none focus:border-primary"
                      />
                    </div>
                  </label>
                  <label className="block min-w-0">
                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Date</span>
                    <input
                      type="date"
                      value={freshSetSummary.event_date}
                      onChange={(e) => setFreshSetSummary((prev) => ({ ...prev, event_date: e.target.value }))}
                      className="mt-1 h-12 w-full min-w-0 rounded-xl border border-gray-200 bg-white px-3 text-sm font-semibold text-gray-900 outline-none focus:border-primary"
                    />
                  </label>
                  {freshSetModal === 'similar' && (
                    <div className="block min-w-0 sm:col-span-2">
                      <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Inspiration theme</span>
                      <p className="mt-1 rounded-xl border border-gray-200 bg-white px-3 py-3 text-sm font-semibold text-gray-900">
                        {currentTheme?.name || freshSetSummary.theme_input || 'Selected theme'}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <p className="mb-2 text-xs font-bold uppercase tracking-[0.16em] text-gray-500">Theme options</p>
                <div className="grid grid-cols-2 gap-3">
                  {[2, 4].map((count) => {
                    const isActive = freshSetCount === count;
                    return (
                      <button
                        key={count}
                        type="button"
                        onClick={() => setFreshSetCount(count)}
                        className={`min-h-14 rounded-2xl border px-4 text-base font-extrabold transition ${
                          isActive
                            ? 'border-primary bg-primary text-white shadow-md'
                            : 'border-gray-200 bg-white text-gray-700 hover:border-primary/40 hover:bg-primary/5'
                        }`}
                      >
                        {count} themes
                      </button>
                    );
                  })}
                </div>
              </div>

            </div>
            <div className="flex shrink-0 flex-col-reverse gap-3 border-t border-gray-100 px-5 py-4 sm:flex-row sm:justify-end sm:px-6">
              <button
                type="button"
                onClick={() => setFreshSetModal(null)}
                className="h-12 rounded-2xl bg-gray-100 px-5 text-sm font-bold text-gray-800 transition hover:bg-gray-200"
              >
                Not now
              </button>
              <button
                type="button"
                onClick={startFreshSetFromModal}
                className="h-12 rounded-2xl bg-primary px-6 text-sm font-extrabold text-white shadow-sm transition hover:bg-primary-dark"
              >
                Generate {freshSetCount} themes
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      <ThemeSelectionModal
        isOpen={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        onConfirm={handleConfirmSelection}
        initialPreferences={selectionPreferences}
        eventData={eventData}
        theme={currentTheme}
        sidebarCollapsed={sidebarCollapsed}
      />
      {pendingDeleteGroup && typeof document !== 'undefined' && createPortal(
        <div
          className="fixed inset-0 z-[100000] flex items-center justify-center bg-slate-950/45 px-4 py-6 backdrop-blur-sm"
          onClick={() => setPendingDeleteGroup(null)}
        >
          <div
            className="w-full max-w-md rounded-3xl border border-gray-200 bg-white p-5 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <h3 className="text-xl font-extrabold text-gray-950">Remove theme set?</h3>
            <p className="mt-2 text-sm leading-6 text-gray-600">
              {pendingDeleteGroup.label} will be removed from this event. Plans tied to those themes stay saved unless you delete the plan separately.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setPendingDeleteGroup(null)}
                className="h-11 rounded-2xl bg-gray-100 px-5 text-sm font-bold text-gray-800 transition hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  handleDeleteThemeGroup(pendingDeleteGroup);
                  setPendingDeleteGroup(null);
                }}
                className="h-11 rounded-2xl bg-red-600 px-5 text-sm font-extrabold text-white shadow-sm transition hover:bg-red-700"
              >
                Remove set
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      {confirmDialog && typeof document !== 'undefined' && createPortal(
        <div
          className="fixed inset-0 z-[100000] flex items-center justify-center bg-slate-950/45 px-4 py-6 backdrop-blur-sm"
          onClick={() => {
            const onCancel = confirmDialog.onCancel;
            setConfirmDialog(null);
            onCancel?.();
          }}
        >
          <div
            className="w-full max-w-md rounded-3xl border border-gray-200 bg-white p-5 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <h3 className="text-xl font-extrabold text-gray-950">{confirmDialog.title}</h3>
            <p className="mt-2 text-sm leading-6 text-gray-600">{confirmDialog.message}</p>
            <div className="mt-6 flex flex-row justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  const onCancel = confirmDialog.onCancel;
                  setConfirmDialog(null);
                  onCancel?.();
                }}
                className="h-11 rounded-2xl bg-gray-100 px-5 text-sm font-bold text-gray-800 transition hover:bg-gray-200"
              >
                {confirmDialog.cancelText || 'Cancel'}
              </button>
              <button
                type="button"
                onClick={() => {
                  const onConfirm = confirmDialog.onConfirm;
                  setConfirmDialog(null);
                  onConfirm?.();
                }}
                className={`h-11 rounded-2xl px-5 text-sm font-extrabold text-white shadow-sm transition ${
                  confirmDialog.variant === 'danger'
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-primary hover:bg-primary-dark'
                }`}
              >
                {confirmDialog.confirmText || 'Continue'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {showDetailsModal && currentTheme && typeof document !== 'undefined' && createPortal(
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100000] flex items-end justify-center overflow-hidden p-0 sm:items-center sm:p-4 sm:overflow-y-auto"
          onClick={() => setShowDetailsModal(false)}
        >
          <div 
            className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-[800px] xl:max-w-[900px] relative shadow-2xl animate-slide-up flex flex-col overflow-hidden"
            style={{
              maxHeight: 'calc(100dvh - 0.5rem)',
              transform: `translate3d(${modalDragOffset}px, 0, 0)`,
              transition: isModalDragging ? 'none' : 'transform 220ms cubic-bezier(0.16, 1, 0.3, 1)'
            }}
            onClick={(e) => e.stopPropagation()}
            onTouchStart={handleModalTouchStart}
            onTouchMove={handleModalTouchMove}
            onTouchEnd={handleModalTouchEnd}
            onTouchCancel={handleModalTouchCancel}
          >
            {/* Header */}
            <div className="flex items-start justify-between border-b border-gray-100 px-4 pt-3 pb-2.5 sm:items-center sm:px-5 sm:pt-4 sm:pb-3">
              {/* Prev / Next within modal */}
              <button
                type="button"
                onClick={() => setCurrentIndex((prev) => Math.max(0, prev - 1))}
                disabled={currentIndex === 0}
                className="hidden sm:inline-flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 text-gray-500 hover:bg-gray-100 disabled:opacity-30 transition shrink-0"
                aria-label="Previous theme"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              <div className="min-w-0 flex-1 pr-3 sm:px-3">
                <div className="flex items-center gap-1.5">
                  <h3 className="text-base sm:text-lg font-extrabold text-gray-900 leading-tight font-sans whitespace-normal break-words">
                    {currentTheme.name}
                  </h3>
                  {onToggleThemeFavorite && (
                    <button
                      type="button"
                      onClick={() => onToggleThemeFavorite(getThemeKey(currentTheme))}
                      className={`shrink-0 transition-all ${
                        favoriteThemeKeys.includes(getThemeKey(currentTheme))
                          ? 'text-amber-500 hover:text-amber-600'
                          : 'text-gray-300 hover:text-amber-400'
                      }`}
                      title={favoriteThemeKeys.includes(getThemeKey(currentTheme)) ? "Remove from Favorites" : "Add to Favorites"}
                    >
                      <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
                        <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                      </svg>
                    </button>
                  )}
                </div>
                <p className="text-[11px] text-gray-400 font-sans">
                  {currentIndex + 1} of {carouselThemes.length} · Theme details
                </p>
              </div>

              <button
                type="button"
                onClick={() => setCurrentIndex((prev) => Math.min(carouselThemes.length - 1, prev + 1))}
                disabled={currentIndex === carouselThemes.length - 1}
                className="hidden sm:inline-flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 text-gray-500 hover:bg-gray-100 disabled:opacity-30 transition shrink-0"
                aria-label="Next theme"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              <button
                type="button"
                onClick={() => setShowDetailsModal(false)}
                className="inline-flex h-9 shrink-0 items-center justify-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 text-xs font-bold text-gray-600 transition hover:bg-gray-50 hover:text-gray-950 sm:ml-2 sm:h-8 sm:w-8 sm:border-0 sm:px-0 sm:text-gray-400 sm:hover:bg-gray-100"
                aria-label="Close theme details"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span className="sm:hidden">Close</span>
              </button>
            </div>
            
            {/* 2-column body: Left=images, Right=content */}
            <div className="flex flex-col sm:flex-row flex-1 overflow-y-auto sm:overflow-hidden">

              {/* LEFT: Image Gallery */}
              <div className="w-full sm:w-[44%] shrink-0 flex flex-col bg-gray-950 sm:overflow-hidden">
                {themeImages.length > 0 ? (
                  <div className="relative w-full h-[220px] sm:h-auto sm:flex-1 group/modal-img flex items-center justify-center overflow-hidden">
                    <img
                      src={themeImages[modalImgIdx]}
                      alt={`${currentTheme.name} — ${modalImgIdx + 1}`}
                      className="w-full h-full object-contain transition-all duration-300 block"
                    />

                    {/* Pagination bubble */}
                    {themeImages.length > 1 && (
                      <div className="absolute top-2.5 right-2.5 bg-black/70 text-white text-[10px] font-bold px-2 py-0.5 rounded-full font-sans">
                        {modalImgIdx + 1} / {themeImages.length}
                      </div>
                    )}

                    {/* Overlay arrows */}
                    {themeImages.length > 1 && (
                      <>
                        <button
                          type="button"
                          onClick={() => setModalImgIdx((prev) => (prev === 0 ? themeImages.length - 1 : prev - 1))}
                          className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 text-white p-1.5 rounded-full shadow transition opacity-0 group-hover/modal-img:opacity-100"
                          aria-label="Previous image"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          onClick={() => setModalImgIdx((prev) => (prev === themeImages.length - 1 ? 0 : prev + 1))}
                          className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 text-white p-1.5 rounded-full shadow transition opacity-0 group-hover/modal-img:opacity-100"
                          aria-label="Next image"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </>
                    )}

                    {/* Dot indicators */}
                    {themeImages.length > 1 && (
                      <div className="absolute bottom-2.5 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
                        {themeImages.map((_, dotIdx) => (
                          <button
                            key={dotIdx}
                            type="button"
                            onClick={() => setModalImgIdx(dotIdx)}
                            className={`h-1.5 rounded-full transition-all ${
                              modalImgIdx === dotIdx ? 'w-4 bg-white' : 'w-1.5 bg-white/40 hover:bg-white/70'
                            }`}
                            aria-label={`Image ${dotIdx + 1}`}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  // No image — show a theme-colored placeholder
                  <div
                    className="flex-1 min-h-[140px] sm:min-h-0 flex flex-col items-center justify-center p-6 gap-2"
                    style={{ background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)' }}
                  >
                    <span className="text-white/20 text-5xl select-none">✦</span>
                    <span className="text-white/50 text-xs font-semibold text-center font-sans">{currentTheme.name}</span>
                  </div>
                )}

                {/* Planner Badges — shown at bottom of left column on sm+ */}
                {currentTheme.planner_badges && currentTheme.planner_badges.length > 0 && (
                  <div className="px-4 py-3 border-t border-white/10 flex flex-wrap gap-1.5">
                    {currentTheme.planner_badges.map((badge, idx) => (
                      <span key={idx} className="px-2.5 py-1 bg-white/10 text-white/80 text-[10px] font-semibold rounded-full font-sans">
                        {badge}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* RIGHT: Content */}
              <div className="flex-1 flex flex-col sm:overflow-y-auto divide-y divide-gray-100">

                {/* Theme Concept */}
                {currentTheme.description && (
                  <div className="px-5 py-3.5">
                    <span className="block text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1 font-sans">Theme Concept</span>
                    <p className="text-sm text-gray-700 leading-relaxed font-sans">{currentTheme.description}</p>
                  </div>
                )}

                {/* Color Palette */}
                {currentTheme.color_palette && currentTheme.color_palette.length > 0 && (
                  <div className="px-5 py-3.5">
                    <div className="flex items-center justify-between gap-2 mb-2.5">
                      <h4 className="text-[10px] font-bold uppercase tracking-wider text-gray-400 font-sans">Color Palette</h4>
                      {onEditTheme && (
                        <button
                          type="button"
                          onClick={() => { onEditTheme(currentTheme); }}
                          className="text-[11px] font-bold text-primary hover:text-primary-dark transition font-sans"
                        >
                          Edit colors
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-col">
                      {currentTheme.color_palette.map((color, idx) => {
                        const colorHex = typeof color === 'string' ? color : color.hex;
                        const isLight = (() => {
                          try {
                            const hex = colorHex.replace('#', '');
                            const r = parseInt(hex.substring(0, 2), 16);
                            const g = parseInt(hex.substring(2, 4), 16);
                            const b = parseInt(hex.substring(4, 6), 16);
                            return (r + g + b) > 630;
                          } catch { return false; }
                        })();
                        return (
                          <div key={idx} className="flex min-w-0 items-center gap-2 rounded-xl bg-gray-50 px-2 py-1.5 font-sans sm:bg-transparent sm:px-0 sm:py-0">
                            <div
                              className={`rounded-full shrink-0 ${isLight ? 'border border-gray-300' : 'border border-white/60'}`}
                              style={{ backgroundColor: colorHex, width: 22, height: 22 }}
                            />
                            {color?.name && color.name !== colorHex ? (
                              <span className="min-w-0 whitespace-normal break-words text-[12px] font-semibold text-gray-800 leading-tight">
                                {color.name}
                              </span>
                            ) : (
                              <span className="min-w-0 whitespace-normal break-words text-[11px] font-mono text-gray-500">{colorHex}</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Plan Anchors */}
                {currentTheme.sample_items && currentTheme.sample_items.length > 0 && (
                  <div className="px-5 py-3.5">
                    <h4 className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-2 font-sans">Plan Anchors (Supplies &amp; Decor)</h4>
                    <div className="flex flex-wrap gap-1.5">
                      {currentTheme.sample_items.map((item, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-semibold bg-stone-100 text-zinc-800 border border-stone-200 font-sans"
                        >
                          <span className="h-1.5 w-1.5 rounded-full bg-zinc-400 shrink-0" />
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Planner Fit Score */}
                {currentTheme.critic_score && (
                  <div className="px-5 py-3.5">
                    <div className="flex items-center gap-3 bg-emerald-50/60 border border-emerald-100 rounded-xl p-3">
                      <div className="flex flex-col items-center justify-center bg-white border border-emerald-200 h-14 w-14 rounded-xl shadow-sm shrink-0">
                        <span className="text-xl font-extrabold text-emerald-600 leading-none">
                          {Number(currentTheme.critic_score).toFixed(1)}
                        </span>
                        <span className="text-[8px] font-bold text-emerald-500 uppercase mt-0.5">fit score</span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <span className="block text-[10px] font-bold uppercase tracking-wider text-emerald-800 mb-0.5 font-sans">Planner Fit Review</span>
                        <p className="text-[11px] text-emerald-900/80 leading-relaxed font-sans">
                          A comprehensive 10-point check analyzing budget realism, visual cohesion, age/audience appropriateness, logistics sanity, and sourcing availability for your specific event setup.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-100 px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom,0px))] sm:px-5 sm:pb-3 shrink-0">
              <div className="hidden items-center justify-between gap-3 sm:flex">
                <span className="text-[11px] text-gray-400 font-sans">{currentIndex + 1} / {carouselThemes.length}</span>
                <button
                  type="button"
                  onClick={() => setShowDetailsModal(false)}
                  className="h-10 px-6 bg-primary text-white font-bold text-sm rounded-xl shadow-sm hover:bg-primary-dark transition font-sans"
                >
                  Done
                </button>
              </div>

              <div className="flex items-center justify-between gap-2 sm:hidden">
                <button
                  type="button"
                  onClick={() => setCurrentIndex((prev) => Math.max(0, prev - 1))}
                  disabled={currentIndex === 0}
                  className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-700 shadow-sm transition hover:bg-gray-50 disabled:opacity-30"
                  aria-label="Previous theme"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>

                <span className="inline-flex min-w-[72px] items-center justify-center rounded-full bg-gray-100 px-3 py-1.5 text-[11px] font-bold text-gray-500 font-sans">
                  {currentIndex + 1} / {carouselThemes.length}
                </span>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setCurrentIndex((prev) => Math.min(carouselThemes.length - 1, prev + 1))}
                    disabled={currentIndex === carouselThemes.length - 1}
                    className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-700 shadow-sm transition hover:bg-gray-50 disabled:opacity-30"
                    aria-label="Next theme"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowDetailsModal(false)}
                    className="h-11 rounded-full bg-primary px-5 text-sm font-bold text-white shadow-sm transition hover:bg-primary-dark font-sans"
                  >
                    Done
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ThemeCarousel;
